import { useForm, getFormProps, getInputProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { type SEOHandle } from '@nasa-gcn/remix-seo'
import {
	json,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { CheckboxField, ErrorList, Field } from '#app/components/forms.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc'
import { PasswordSchema, UsernameSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

export const handle: SEOHandle = {
	getSitemapEntries: () => null,
}

const LoginFormSchema = z.object({
	username: UsernameSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
	remember: z.boolean().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return json({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = await parseWithZod(formData, {
		schema: (intent) =>
			LoginFormSchema.transform(async (data, ctx) => {
				if (intent !== null) return { ...data, session: null }

				const session = await login(data)
				if (!session) {
					ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: 'Invalid username or password',
					})
					return z.NEVER
				}

				return { ...data, session }
			}),
		async: true,
	})

	if (submission.status !== 'success' || !submission.value.session) {
		return json(
			{
				result: submission.reply({
					hideFields: ['password'],
				}),
			},
			{
				status: submission.status === 'error' ? 400 : 200,
			},
		)
	}

	const { session, remember, redirectTo } = submission.value

	return handleNewSession({
		request,
		session,
		remember: remember ?? false,
		redirectTo,
	})
}

export default function LoginPage() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo') ?? '/app/dashboard'

	const [form, fields] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex flex-col space-y-6">
			<div className="space-y-4">
				<h1 className="text-4xl font-bold">Connexion</h1>
				<p className="pb-4 pt-4 text-2xl text-muted-foreground">
					Connectez-vous pour accéder à votre compte.
				</p>

				<Form method="POST" {...getFormProps(form)}>
					<HoneypotInputs />
					<Field
						labelProps={{ children: "Nom d'utilisateur" }}
						inputProps={{
							placeholder: "Entrez votre nom d'utilisateur",
							...getInputProps(fields.username, { type: 'text' }),
							autoFocus: true,
							className: 'mt-3 text-lg py-7',
							autoComplete: 'username',
							tabIndex: 0,
						}}
						className="gap-4"
						errors={fields.username.errors}
					/>
					<Field
						labelProps={{ children: 'Mot de passe' }}
						inputProps={{
							placeholder: 'Entrez votre mot de passe',
							...getInputProps(fields.password, {
								type: 'password',
							}),
							autoComplete: 'current-password',
							className: 'mt-3 text-lg py-7',
							type: 'password',
							tabIndex: 0,
						}}
						auxiliaryProps={{
							children: (
								<Link
									to="/forgot-password"
									className="float-right text-sm"
									tabIndex={1}
								>
									Mot de passe oublié?
								</Link>
							),
						}}
						errors={fields.password.errors}
					/>

					<input {...getInputProps(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />

					<div className="flex items-center justify-between">
						<StatusButton
							className="text-md mb-4 p-6"
							status={isPending ? 'pending' : (form.status ?? 'idle')}
							type="submit"
							disabled={isPending}
						>
							Se connecter
						</StatusButton>
						<CheckboxField
							labelProps={{
								htmlFor: fields.remember.id,
								children: 'Se souvenir de moi',
							}}
							buttonProps={getInputProps(fields.remember, {
								type: 'checkbox',
							})}
							errors={fields.remember.errors}
						/>
					</div>
				</Form>
			</div>
			<p className="text-sm text-gray-500">
				© 2025 <Link to="">PLQ.</Link> Tous droits réservés.
			</p>
		</div>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Connexion à PLQ' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
